import { conditionToken, diseaseCategoryToken } from '@/mixins/clinicalTranslations'
import { getCategoryColors } from '../../../utils/conditionsCoveredColors'
import { getConditionCategories } from './categoryUtils'

// patientType slugs used for track identification

const TRACK_ORDER = [
  'newborn',
  'child',
  'youth',
  'adult',
  'antenatal',
  'postnatal',
]

const TRIAGE_PRIORITY = ['immediate', 'urgent', 'standard', 'inconclusive', 'none']

export function useInsightsData(visits, allMrxConditions) {

  let _mrxConditionMapCache = null

  function buildPositiveAssessments(expandedAssessments) {
    const map = {}

    visits.value.forEach((visit) => {
      if (!visit.conditions) {
        return
      }

      const track = visit.patientType

      visit.conditions.forEach((conditionObj) => {
        const condition = conditionObj.name

        if (!map[condition]) {
          map[condition] = {
            name: condition,
            token: conditionToken(condition),
            diseaseCategories: getConditionCategories(conditionObj).map((name) => {
              const colors = getCategoryColors(name)

              return {
                name,
                token: diseaseCategoryToken(name),
                categoryColor: colors?.bg || '#60A5FA',
                categoryTextColor: colors?.text || '#FFFFFF'
              }
            }),
            tracks: [],
            totalCases: 0,
            breakdown: {},
            visits: [],
            expanded: !!expandedAssessments[condition],
          }
        }

        const entry = map[condition]

        entry.totalCases++

        if (!entry.tracks.includes(track)) {
          entry.tracks.push(track)
        }

        if (!entry.breakdown[track]) {
          entry.breakdown[track] = 0
        }

        entry.breakdown[track]++

        entry.visits.push(visit)
      })
    })

    return Object.values(map).map((assessment) => {
      const breakdown = Object.entries(assessment.breakdown).map(([track, count]) => {
        return { track, count }
      })

      return {
        ...assessment,
        breakdown,
      }
    })
  }

  function buildMrxConditionMap() {
    if (_mrxConditionMapCache) return _mrxConditionMapCache

    const map = {}

    allMrxConditions.value.forEach((c) => {
      if (!c.name) {
        return
      }

      map[c.name.toLowerCase()] = c
    })

    _mrxConditionMapCache = map
    return map
  }

  function buildNegativeAssessments() {
    const trackMap = {}
    const conditionMap = buildMrxConditionMap()

    visits.value.forEach((visit) => {

      const ruledOut =
        visit.detailData && visit.detailData.noRiskFound
          ? visit.detailData.noRiskFound
          : []

      if (!ruledOut.length) {
        return
      }

      const track = visit.patientType

      if (!trackMap[track]) {
        trackMap[track] = {}
      }

      ruledOut.forEach((conditionName) => {

        if (!trackMap[track][conditionName]) {

          const mrx = conditionMap[conditionName.toLowerCase()]

          trackMap[track][conditionName] = {
            name: conditionName,
            token: conditionToken(conditionName),
            categories: mrx ? mrx.categories.map(cat => ({
              name: cat,
              token: diseaseCategoryToken(cat)
            })) : [],
          }
        }

      })
    })

    const result = {}

    Object.keys(trackMap).forEach((track) => {
      const categoryMap = {}

      Object.values(trackMap[track]).forEach((neg) => {
        const firstCat = neg.categories && neg.categories.length
          ? neg.categories[0]
          : { name: 'Other', token: '' }

        if (!categoryMap[firstCat.name]) {
          categoryMap[firstCat.name] = {
            categoryName: firstCat.name,
            categoryToken: firstCat.token,
            conditions: [],
          }
        }

        categoryMap[firstCat.name].conditions.push({ name: neg.name, token: neg.token })
      })

      const grouped = Object.values(categoryMap)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName))
        .map((group) => ({
          ...group,
          conditions: group.conditions.sort((a, b) => a.name.localeCompare(b.name)),
        }))

      if (grouped.length) {
        result[track] = grouped
      }
    })

    return result
  }

  function buildTrackGroups(expandedAssessments) {
    const positives = buildPositiveAssessments(expandedAssessments)
    const negatives = buildNegativeAssessments()

    const groups = {}

    positives.forEach((assessment) => {

      assessment.tracks.forEach((track) => {

        if (!groups[track]) {
          groups[track] = {
            track,
            assessments: [],
            negatives: [],
            total: 0,
          }
        }

        const breakdown = assessment.breakdown.find((b) => b.track === track)

        const trackCount = breakdown ? breakdown.count : 0

        const trackVisits = assessment.visits.filter((v) => v.patientType === track)

        const triageByConditions = Object.values(
          trackVisits.reduce((acc, visit) => {
            visit.conditions
              .filter((condition) => condition.name === assessment.name)
              .forEach((condition) => {
                const triage = condition.triage_recommendation
                if (!acc[triage]) {
                  acc[triage] = { triage_recommendation: triage, total: 0 }
                }
                acc[triage].total++
              })
            return acc
          }, {})
        )

        triageByConditions.sort((a, b) =>
          TRIAGE_PRIORITY.indexOf(a.triage_recommendation) - TRIAGE_PRIORITY.indexOf(b.triage_recommendation)
        )

        const topTriage = triageByConditions.length === 0
          ? ""
          : triageByConditions[0].triage_recommendation

        groups[track].assessments.push({
          ...assessment,
          track,
          totalCases: trackCount,
          visits: trackVisits,
          topTriage,
          triageByConditions: triageByConditions,
          direction: topTriage === 'standard' || topTriage === 'none' ? 'negative_dir' : 'positive',
        })

        groups[track].total += trackCount
      })
    })

    Object.keys(negatives).forEach((track) => {
      if (!groups[track]) {
        groups[track] = {
          track,
          assessments: [],
          negatives: [],
          total: 0,
        }
      }

      const positiveNames = new Set(groups[track].assessments.map((a) => a.name.toLowerCase()))

      const filtered = (negatives[track] || []).reduce((acc, group) => {
        const conditions = group.conditions.filter((c) => !positiveNames.has(c.name.toLowerCase()))
        if (conditions.length) {
          acc.push({ ...group, conditions })
        }
        return acc
      }, [])

      groups[track].negatives = filtered
    })

    return TRACK_ORDER
      .filter((track) => groups[track])
      .map((track) => ({
        ...groups[track],
        assessments: [...groups[track].assessments].sort((a, b) => a.name.localeCompare(b.name)),
      }))
  }

  return {
    buildTrackGroups,
  }
}
